export interface UserProfile {
	id: number;
	Anrede: string;
	Titel: string;
	Vorname: string;
	Name: string;
	Anrede2?: string;
	Vorname2?: string;
	Name2?: string;
	Land: string;
	Plz: string;
	Ort: string;
	Strasse: string;
	Email: string;
	Telefon1: string;
	telefonInfo: string | null;
}

export type OwnedEstate = {
	estate_id: number;
	status: string;
	objekttitel: string | null;
	address: string;
	kaufpreis: number | string | null;
	objektart: string | null;
	vermarktungsart: string | null;
	kaltmiete: number | string | null;
	nebenkosten: number | string | null;
	miete_einbaukueche: number | string | null;
	wordpress_url: string | null;
	cover_image_filename: string | null;
	contact: {
		name: string;
		position: string;
		image_url: string | null;
		phone: string | null;
		mobile: string | null;
		email: string | null;
	} | null;
	objekttracking: {
		appointmentCount: number;
		besichtigungstermineCount: number;
	} | null;
};

export type OwnedEstatesOverview = {
	count: number;
	estates: OwnedEstate[];
};

export type AgentslogEntry = Record< string, unknown > & {
	id: number;
	Aktionsart: string;
	Aktionstyp: string;
	Aktionsmerkmale: string[];
	merkmal: string;
	Datum?: string;
	dauer?: string;
	created?: string;
	Bemerkung?: string;
	Absagegrund?: string;
	// number of interested parties registered for an "Offene Besichtigung"
	// on the same calendar day (attached server-side).
	interessentenCount?: number;
	// number of "Selektionsversand" emails aggregated for the same day
	// (attached server-side).
	selektionsversandCount?: number;
};

export type OwnedEstateDetails = {
	estate: OwnedEstate;
	groups: {
		termine: AgentslogEntry[];
		protokoll: AgentslogEntry[];
		marketing: AgentslogEntry[];
	};
};

class UserService {
	private baseUrl: string;
	private endpoints: Record< string, string >;

	constructor() {
		this.baseUrl = '';
		this.endpoints = {
			register: '/wp-json/dhsv/v1/user-register',
			userProfile: '/wp-json/dhsv/v1/user-profile',
			ownedEstates: '/wp-json/dhsv/v1/owned-estates',
		};
	}

	/**
	 * Registers a new user with email, password, and profile data.
	 * @param {string} email    - The user's email.
	 * @param {string} password - The user's password.
	 * @param {Object} profile  - Profile data like { Vorname: 'Thorsten' }.
	 * @return {Promise<object>} Response from the API.
	 */
	async registerUser(
		email: string,
		password: string,
		profile: Record< string, string >
	): Promise< object > {
		const url = `${ this.baseUrl }${ this.endpoints.register }`;
		const payload = { email, password, profile };

		try {
			const response = await fetch( url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify( payload ),
			} );

			if ( ! response.ok ) {
				// try to parse WordPress REST error { code, message, data: { status } }
				let errorPayload: unknown = null;
				const text = await response.text();
				try {
					errorPayload = JSON.parse( text );
				} catch {
					// ignore JSON parse errors
				}
				// shape from WP_Error
				const wpErr = errorPayload as {
					code?: string;
					message?: string;
					data?: { status?: number };
				} | null;
				if (
					wpErr?.code === 'user_already_exists' ||
					wpErr?.data?.status === 422
				) {
					throw new Error(
						'Diese E-Mail ist bereits registriert. Bitte melden Sie sich an.'
					);
				}
				throw new Error(
					wpErr?.message ||
						`Registration failed: ${ response.status } ${ response.statusText }`
				);
			}

			return await response.json();
		} catch ( error: unknown ) {
			// eslint-disable-next-line no-console
			console.error(
				'Error during user registration:',
				( error as Error ).message
			);
			throw error;
		}
	}

	/**
	 * Fetches the user's profile data.
	 * @param {string} token - Authorization token passed as 'X-Supabase-Token'.
	 * @return {Promise<object>} User profile data.
	 */
	async getUserProfile( token: string ): Promise< UserProfile > {
		const url = `${ this.baseUrl }${ this.endpoints.userProfile }`;

		try {
			const response = await fetch( url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'X-Supabase-Token': token,
				},
			} );

			if ( ! response.ok ) {
				throw new Error(
					`Failed to fetch user profile: ${ response.statusText }`
				);
			}

			return await response.json();
		} catch ( error: unknown ) {
			// eslint-disable-next-line no-console
			console.error(
				'Error fetching user profile:',
				( error as Error ).message
			);
			throw error;
		}
	}

	/**
	 * Updates the user's profile data.
	 * @param {Object} profile - Updated profile data to be sent.
	 * @param {string} token   - Authorization token passed as 'X-Supabase-Token'.
	 * @return {Promise<object>} API response after update.
	 */
	async updateUserProfile(
		profile: Record< string, string >,
		token: string
	): Promise< object > {
		const url = `${ this.baseUrl }${ this.endpoints.userProfile }`;
		const payload = { profile };

		try {
			const response = await fetch( url, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'X-Supabase-Token': token,
				},
				body: JSON.stringify( payload ),
			} );

			if ( ! response.ok ) {
				throw new Error(
					`Profile update failed: ${ response.statusText }`
				);
			}

			return await response.json();
		} catch ( error: unknown ) {
			// eslint-disable-next-line no-console
			console.error(
				'Error updating user profile:',
				( error as Error ).message
			);
			throw error;
		}
	}

	/**
	 * Fetches the estates owned by the current user (Objekttracking).
	 * @param token
	 */
	async getOwnedEstates( token: string ): Promise< OwnedEstatesOverview > {
		const url = `${ this.baseUrl }${ this.endpoints.ownedEstates }`;
		const response = await fetch( url, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'X-Supabase-Token': token,
			},
		} );

		if ( ! response.ok ) {
			throw new Error(
				`Failed to fetch owned estates: ${ response.statusText }`
			);
		}

		return await response.json();
	}

	/**
	 * Fetches Objekttracking details for one owned estate.
	 * @param estateId
	 * @param token
	 */
	async getOwnedEstateDetails(
		estateId: number,
		token: string
	): Promise< OwnedEstateDetails > {
		const url = `${ this.baseUrl }${ this.endpoints.ownedEstates }/${ estateId }`;
		const response = await fetch( url, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'X-Supabase-Token': token,
			},
		} );

		if ( ! response.ok ) {
			throw new Error(
				`Failed to fetch owned estate details: ${ response.statusText }`
			);
		}

		return await response.json();
	}

	/**
	 * Triggers the "Exposé anfordern" process in onOffice by creating an activity.
	 * @param estateId
	 * @param token
	 */
	async requestOwnedEstateExpose(
		estateId: number,
		token: string
	): Promise< { ok: true } > {
		const url = `${ this.baseUrl }${ this.endpoints.ownedEstates }/${ estateId }/request-expose`;
		const response = await fetch( url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Supabase-Token': token,
			},
		} );

		if ( ! response.ok ) {
			throw new Error(
				`Failed to request expose: ${ response.statusText }`
			);
		}

		return await response.json();
	}

	/**
	 * Logs a login activity in onOffice
	 * @param token
	 */
	async trackLogin( token: string ): Promise< { ok: true } > {
		const url = `${ this.baseUrl }${ this.endpoints.ownedEstates }/track-login`;
		const response = await fetch( url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Supabase-Token': token,
			},
		} );

		if ( ! response.ok ) {
			throw new Error(
				`Failed to track owner login: ${ response.statusText }`
			);
		}

		return await response.json();
	}
}

export default UserService;
